/** Plan de cuentas → «Cuentas de los asientos automáticos»: qué cuenta usa cada papel en las facturas, cobros y provisiones. */
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { api } from '../../lib/api'
import { apiMessage } from './EntryModal'
import { td, th } from '../accounting/common'
import {
  CTB_ACCOUNT_DEFAULTS,
  CTB_ACCOUNT_ROLES,
  CTB_ACCOUNT_ROLE_LABEL,
  type CtbAccountMapDto,
  type CtbAccountRole,
} from '@toga/shared'

export default function AccountMapCard({ issuerId }: { issuerId: string }) {
  const [data, setData] = useState<CtbAccountMapDto | null>(null)
  const [draft, setDraft] = useState<Partial<Record<CtbAccountRole, string>>>({})
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const load = useCallback(async () => {
    try {
      setData(await api.get<CtbAccountMapDto>(`/ctb/account-map?issuerId=${issuerId}`))
      setDraft({})
    } catch (err) {
      setError(apiMessage(err, 'No se pudieron cargar las cuentas automáticas'))
    }
  }, [issuerId])
  useEffect(() => {
    void load()
  }, [load])

  if (!data) return null
  const problems = data.checks.filter((c) => c.problem).length
  const valueOf = (role: CtbAccountRole) => draft[role] ?? data.effective[role]
  const dirty = CTB_ACCOUNT_ROLES.some((r) => valueOf(r) !== data.effective[r])

  const save = async (map: Partial<Record<CtbAccountRole, string>>) => {
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      await api.put('/ctb/account-map', { issuerId, map })
      await load()
      setSaved(true)
    } catch (err) {
      setError(apiMessage(err, 'No se pudieron guardar las cuentas'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="surface overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-3 flex items-center gap-2 text-left"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="t-primary font-medium text-sm">Cuentas de los asientos automáticos</span>
        <span className="text-xs t-faint">
          Facturas, cobros y provisiones de fondos las usan para contabilizarse solas
        </span>
        {problems > 0 && (
          <span className="ml-auto text-[11px] text-amber-300 inline-flex items-center gap-1">
            <AlertCircle size={12} /> {problems} por revisar
          </span>
        )}
      </button>
      {open && (
        <div className="border-t border-base">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-base">
                <th className={th}>Papel</th>
                <th className={th}>Cuenta</th>
                <th className={th}>Nombre en el plan</th>
              </tr>
            </thead>
            <tbody>
              {CTB_ACCOUNT_ROLES.map((role) => {
                const check = data.checks.find((c) => c.role === role)
                const changed = valueOf(role) !== data.effective[role]
                return (
                  <tr key={role} className="border-b border-base/50 align-top">
                    <td className={td}>
                      <div className="t-primary">{CTB_ACCOUNT_ROLE_LABEL[role].label}</div>
                      <div className="text-[11px] t-faint">{CTB_ACCOUNT_ROLE_LABEL[role].hint}</div>
                    </td>
                    <td className={td}>
                      <input
                        value={valueOf(role)}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, [role]: e.target.value.replace(/\D/g, '') }))
                        }
                        maxLength={10}
                        inputMode="numeric"
                        aria-label={`Cuenta para ${CTB_ACCOUNT_ROLE_LABEL[role].label}`}
                        className="w-28 bg-transparent border border-base rounded px-2 py-1 text-sm t-primary font-mono"
                      />
                      {valueOf(role) !== CTB_ACCOUNT_DEFAULTS[role] && (
                        <div className="text-[10px] t-faint mt-0.5">
                          Por defecto: {CTB_ACCOUNT_DEFAULTS[role]}
                        </div>
                      )}
                    </td>
                    <td className={td}>
                      {changed ? (
                        <span className="text-[11px] t-faint">Guarda para comprobarla</span>
                      ) : check?.problem ? (
                        <span className="text-xs text-amber-300 inline-flex items-center gap-1">
                          <AlertCircle size={12} /> {check.problem}
                        </span>
                      ) : (
                        <span className="text-xs t-secondary inline-flex items-center gap-1">
                          <CheckCircle2 size={12} className="text-emerald-300" /> {check?.name}
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-t border-base">
            <button
              type="button"
              disabled={busy || !dirty}
              onClick={() => void save(draft2map(data, draft))}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {busy && <Loader2 size={13} className="animate-spin" />}
              Guardar
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void save({})}
              className="text-xs t-muted hover:underline"
            >
              Volver a las de por defecto
            </button>
            {saved && <span className="text-xs text-emerald-300">Guardado</span>}
            {error && <span className="text-xs text-rose-300">{error}</span>}
          </div>
          <p className="px-5 pb-3 text-[11px] t-faint max-w-3xl">
            Los suplidos van a una cuenta aparte, no son ingreso. El criterio contable (555 u otra)
            lo tiene que confirmar la gestoría: aquí se puede cambiar sin tocar nada más.
          </p>
        </div>
      )}
    </section>
  )
}

/** El mapa completo que se envía: lo vigente con lo editado por encima. */
function draft2map(
  data: CtbAccountMapDto,
  draft: Partial<Record<CtbAccountRole, string>>
): Partial<Record<CtbAccountRole, string>> {
  const out: Partial<Record<CtbAccountRole, string>> = {}
  for (const role of CTB_ACCOUNT_ROLES) out[role] = draft[role] ?? data.effective[role]
  return out
}
